import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { X, ChevronLeft, ChevronRight, Package } from "lucide-react";

export default function PresentationPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const idsParam = params.get("ids") || "";
  const [products, setProducts] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    (async () => {
      const ids = idsParam.split(",").filter(Boolean);
      const results = [];
      for (const id of ids) {
        try { const { data } = await api.get(`/products/${id}`); results.push(data); } catch {}
      }
      setProducts(results);
    })();
  }, [idsParam]);

  const next = useCallback(() => setIdx(i => Math.min(i + 1, products.length - 1)), [products.length]);
  const prev = useCallback(() => setIdx(i => Math.max(i - 1, 0)), []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); next(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
      else if (e.key === "Escape") { nav(-1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, nav]);

  if (!products.length) {
    return (
      <div className="fixed inset-0 bg-[#083F22] text-white flex items-center justify-center" data-testid="presentation-loading">
        Loading presentation…
      </div>
    );
  }

  const p = products[idx];
  const img = p?.media?.[0];

  return (
    <div className="fixed inset-0 bg-[#083F22] text-white flex flex-col" data-testid="presentation-page">
      {/* Top bar */}
      <div className="flex items-center justify-between px-8 py-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9A24B]">Presentation</div>
          <div className="font-serif text-lg">{idx + 1} / {products.length}</div>
        </div>
        <button onClick={() => nav(-1)} className="p-2 rounded-full hover:bg-white/10" data-testid="presentation-close" aria-label="Exit">
          <X size={22}/>
        </button>
      </div>

      {/* Slide */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-8 px-8 pb-8 items-center">
        <div className="aspect-square bg-white/5 rounded-2xl overflow-hidden flex items-center justify-center max-h-[70vh]">
          {img ? (
            <img src={fileUrl(img.url || img.thumb_url)} alt="" className="w-full h-full object-contain" data-testid="presentation-image"/>
          ) : (
            <Package size={64} className="text-[#C9A24B]" strokeWidth={1}/>
          )}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9A24B]">
            {p.design_code || p.sku || "—"} · {p.metal} {p.purity || ""}
          </div>
          <h1 className="font-serif text-5xl mt-2 leading-tight">{p.title}</h1>
          {p.labels?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {p.labels.map(l => <span key={l} className="text-[10px] uppercase tracking-widest bg-[#C9A24B]/25 text-[#F6EED8] px-3 py-1 rounded-full">{l}</span>)}
            </div>
          )}
          <div className="mt-6 grid grid-cols-2 gap-4 text-sm max-w-md">
            <div><div className="text-[10px] uppercase tracking-widest text-[#C9A24B]/80">Weight</div><div className="text-2xl font-serif mt-1">{p.weight_grams ?? p.net_weight ?? "—"} g</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-[#C9A24B]/80">Sub-category</div><div className="text-xl font-serif mt-1">{p.sub_category || p.product_category_name || "—"}</div></div>
            {p.is_price_visible && (
              <div className="col-span-2"><div className="text-[10px] uppercase tracking-widest text-[#C9A24B]/80">Price</div><div className="text-4xl font-serif mt-1 text-[#C9A24B]">₹{(p.computed_price || 0).toLocaleString('en-IN')}</div></div>
            )}
          </div>
          {p.description && <p className="mt-6 text-white/70 leading-relaxed max-w-md">{p.description}</p>}
          {p.stone_details && (
            <div className="mt-4 text-sm text-white/70"><span className="text-[#C9A24B]">Stones:</span> {p.stone_details}</div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between px-8 pb-6">
        <button onClick={prev} disabled={idx === 0} className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30" data-testid="presentation-prev">
          <ChevronLeft size={18}/> Previous
        </button>
        <div className="flex-1 mx-8 h-1 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full bg-[#C9A24B] transition-all" style={{ width: `${((idx + 1) / products.length) * 100}%` }}/>
        </div>
        <button onClick={next} disabled={idx === products.length - 1} className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#C9A24B] hover:bg-[#B08F3D] text-[#083F22] font-medium disabled:opacity-30" data-testid="presentation-next">
          Next <ChevronRight size={18}/>
        </button>
      </div>
    </div>
  );
}
